<script lang="ts">
	import {
		Modal,
		IconButton,
		ModalContentLayout,
		NavigationItem,
		Alert,
		Loader,
		LabelV2,
		Toggle,
		Text,
		LegendContainer,
		Container,
		Input,
	} from '@Shelly/shelly-design-system/index';
	import SidePanelSettingsModalStickyBottom from './SidePanelSettingsModalStickyBottom.svelte';
	import SettingsSearchHighlightedContent from './SettingsSearchHighlightedContent.svelte';
	import NoSearchResults from 'src/components/Lists/NoSearchResults.svelte';
	import { writable } from 'svelte/store';
	import type { NavigationMenuItemExtendedPropsTypes } from '../sidepanel-settings-modal';
	import { devices_config } from '@devices/index';
	import {
		get_mode,
		real_id,
		is_gen_2,
		isWallDisplay,
		is_loqed,
		is_GBLE,
	} from 'src/helpers/interface_helpers';
	import { user_data } from 'src/stores/account/user';
	import { getDeviceSettings, getDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import { getDeviceSettingsReadiness } from 'src/stores/device-runtime/readiness';
	import { deviceRuntimeResource } from 'src/stores/device-runtime/resource-state';
	import { devices } from 'src/stores/lists/devices';
	import { rooms } from 'src/stores/lists/rooms';
	import { app, os } from 'src/stores/session/native-bridge';
	import type { Device } from 'src/types/Device';
	import { sidePanelState } from 'src/stores/ui/sidepanel-public';
	import {
		getCriticalNotifications,
		getNotificationsForDevice,
	} from 'src/helpers/notifications/device_notifications_helper';
	import { onMount, tick, setContext } from 'svelte';
	import { replace } from 'svelte-spa-router';
	import { mobile } from 'src/services/mobile-rework';
	import {
		deviceSettingFormatter,
		handleCollapseTitleLabel,
	} from 'src/helpers/device_settings_helpers';
	import DeviceSettingsItem from 'src/components/DevicesSettings/DeviceSettingsItem.svelte';
	import { format } from 'svelte-i18n';
	import ThermostatsG2 from 'src/components/DevicesSettings/Settings/SettingsG2/ThermostatsWallDisplay/ThermostatsG2.svelte';
	import MediaPlayerComponent from 'src/components/MediaPlayerComponent.svelte';
	import { capFirstChar } from 'src/helpers/utilities';
	import { getDeviceState, rebootRequired } from 'src/stores/device-runtime/states';
	import { isTabDisabled } from '../sidepanel-tabs-helpers';
	import {
		isSettingsRouteInitiate,
		routeToDeviceSettings,
		settingsRouteIntent,
	} from 'src/stores/ui/route-to-device-settings';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import { hasExternalSensors } from 'src/helpers/ext_sensor_helpers';
	import { isSupportedVersion } from 'src/helpers/inclusion_helpers';
	import { controlBleDevice } from 'src/stores/features/ble/control-device';
	import { control_bluetooth_device_ios } from 'src/services/native_container_requests_ios';
	import { control_bluetooth_device_android } from 'src/services/native_container_requests_android';
	import BluetoothSettings from 'src/components/DevicesSettings/SettingsGBLE/BluetoothSettings.svelte';
	import { isXT1Module, resolveXT1ComponentChannel } from 'src/helpers/xt1_helpers/template_helpers';
	import {
		fetchXT1DeviceSettings,
		getXT1SettingsNavigationItems,
		type XT1DeviceSettings,
	} from 'src/helpers/xt1_helpers/template_settings_helpers';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import { homes } from 'src/stores/account/home';
	import { getDeviceImage, getDeviceIcon } from 'src/helpers/device_state_view_helpers';
	import { deviceSettingsAdvanced } from 'src/helpers/settings/device_settings_advanced';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import {
		collectSettingsRailNavIds,
		flattenSettingsRail,
		getSettingsRailPremiumState,
		getSettingsRailRowTitle,
		getSettingsRailSectionForItem,
		isSettingsRailGroupCollapsed,
		isSettingsRailItemAdvanced,
		isSettingsRailRowCollapsed,
		resolveSettingsRailNavId,
		SETTINGS_ACTION_TITLES,
		SETTINGS_SECTION_COPY,
	} from 'src/helpers/settings/flatten_settings_rail';
	import {
		buildSettingsSearchDocuments,
		filterSettingsRailEntries,
		MIN_SETTINGS_SEARCH_QUERY_LENGTH,
		normalizeSettingsSearchText,
		searchSettingsDocuments,
	} from 'src/helpers/settings/device_settings_search_helpers';
	import { highlightSettingsSearchContent } from 'src/helpers/settings/settings_search_content_highlight';
	import deviceSettingsSearchCatalog from 'virtual:device-settings-search-catalog';
	import {
		getSettingsModalPaneFlags,
		getSettingsNavGenerateTab,
		isSettingsNavTabMatch,
	} from 'src/helpers/settings/settings_modal_nav';

	export let showSettingsModal = false;
	export let device: Device;

	$: statusStore = getDeviceStatus(device?.id);
	$: settingsStore = getDeviceSettings(device?.id);
	$: status = $statusStore;
	$: settings = $settingsStore as Record<string, unknown> | undefined;
	$: realID = real_id(device?.id);
	$: mode = get_mode(device, status, settings);
	$: settingsReadiness = getDeviceSettingsReadiness({
		deviceId: device?.id,
		settings,
		resource: $deviceRuntimeResource,
	});
	$: settingsReady = settingsReadiness.ready;
	$: rebootPending =
		Boolean((status as { sys?: { restart_required?: boolean } })?.sys?.restart_required) ||
		$rebootRequired?.id === device?.id;

	$: if ($sidePanelState.showSettingsModal === false && showSettingsModal === true) {
		showSettingsModal = false;
		setSidePanelState({ showSettingsModal: undefined });
	}

	let initialize = true;
	let drillIntoSetting = false;
	let isMobile = false;
	let navigationItems: NavigationMenuItemExtendedPropsTypes[] = [];
	let xt1Settings: XT1DeviceSettings[] = [];
	let settingsSearchQuery = '';
	let settingsSearchDeviceId = '';

	$: if (device?.id !== settingsSearchDeviceId) {
		settingsSearchDeviceId = device?.id ?? '';
		settingsSearchQuery = '';
	}

	enum IconVariants {
		details = 'fa-circle-info',
		location = 'fa-door-open',
		secondary_controls = 'fa-palette',
		ambient_light = 'fa-lightbulb-on',
		effects = 'fa-stars',
		timer = 'fa-clock',
		schedule = 'fa-calendar',
		internet = 'fa-globe',
		networks = 'fa-globe',
		safety = 'fa-shield',
		actions = 'fa-link',
		webhooks = 'fa-link',
		night_mode = 'fa-moon',
		sensor_actions = 'fak fa-light-sensor-on-link',
		application_settings = 'fa-gears',
		fan_settings = 'fa-fan',
		sensor_settings = 'fak fa-light-sensor-on-gear',
		external_sensors = 'fak fa-light-sensor-on',
		plus_uni_counter = 'fa-gauge-circle-plus',
		virtual_components = 'fa-cube',
		bthome_components = 'fa-cube',
		irrigation_zones_settings = 'fa-droplet',
		irrigation_sequences = 'fa-network-wired',
		peripherals = 'fak fa-light-sensor-on',
		addon_sensors = 'fak fa-light-sensor-on',
		sensor_mute = 'fa-bell-slash',
		temperature_action = 'fa-temperature-arrow-down',
		humidity_action = 'fa-droplet-percent',
		adc_actions = 'fa-exchange',
		temperature_automation = 'fa-temperature-high',
		humidity_automation = 'fa-dewpoint',
		adc_automation = 'fa-tachometer-alt-fast',
		favorites = 'fa-star',
		scripts = 'fa-brackets-curly',
		share = 'fa-share-nodes',
		settings = 'fa-cog',
		charts = 'fa-chart-line',
		notifications = 'fa-bell',
		event_log = 'fa-notebook',
		valve = 'fa-pipe-valve',
		self_test = 'fa-clipboard-list-check',
		find_my_device = 'fa-radar',
		ui_settings = 'fa-sidebar-flip',
		thermostat = 'fa-temperature-list',
		media_player = 'fa-music',
		device_information = 'fa-info',
		maintenance_actions = 'fa-screwdriver-wrench',
		transfer_device = 'fa-share-nodes',
		delete_device = 'fa-trash',
		geo_location_and_elevation = 'fa-map-location-dot',
		pill_peripheral_settings = 'fa-gauge-circle-plus',
		bluetooth = 'fa-bluetooth',
		bluetooth_gateway = 'fa-bluetooth',
		motion_settings = 'fa-sensor-on',
		camera_audio = 'fa-volume',
		camera_and_video = 'fa-video',
		camera_picture = 'fa-image',
		tariff_configuration = 'fa-tags',
		camera_zones = 'fa-square-dashed-circle-plus',
		camera_recordings = 'fa-clapperboard-play',
		camera_storage = 'fa-hard-drive',
	}

	const getIconFromTitle = (title: string) => {
		let icon = IconVariants[title];
		if (icon == undefined) {
			return 'fal fa-cog';
		} else if (icon.includes('fak')) {
			return icon;
		} else if (icon.includes('fa-palette')) {
			return 'fa-light ' + icon;
		} else {
			return 'fal ' + icon;
		}
	};

	$: settingsDevice = resolveXT1ComponentChannel(device);
	$: isXT1Device = isXT1Module(device?.type);
	$: settingsMode = isXT1Device && !devices_config[device.type]?.modes?.[mode] ? 'module' : mode;

	const buildDeviceSettings = (
		currentDevice: Device,
		currentSettingsMode: string,
		currentSettings: Record<string, unknown> | undefined,
		currentMode: string,
	) => {
		let configSettings =
			devices_config[currentDevice.type]?.modes[currentSettingsMode]?.settings || [];

		const external_sensors = configSettings?.find(
			(item: { title: string }) => item.title == 'external_sensors',
		);

		if (external_sensors) {
			if (!hasExternalSensors(currentDevice)) {
				configSettings = configSettings.filter(
					(item: { title: string }) =>
						!/external_sensors|temperature_automation|humidity_automation|sensor_actions/.test(
							item.title,
						),
				);
			} else if (!hasExternalSensors(currentDevice, 'humidity')) {
				configSettings = configSettings.filter(
					(item: { title: string }) => !/humidity_automation/.test(item.title),
				);
			}
		}

		const isWallDisplayThermostatEnabled = Object.keys(currentSettings ?? {}).some((key) =>
			key.startsWith('thermostat'),
		);

		// Add sensor settings tab for wall display main device if it doesn't exist
		if (isWallDisplay(currentDevice?.type) && !currentDevice?.id?.includes('_')) {
			const hasSensorSettings = configSettings.some((item) => item.title === 'sensor_settings');
			if (isWallDisplayThermostatEnabled && !hasSensorSettings) {
				configSettings.splice(configSettings.length - 6, 0, {
					title: 'sensor_settings',
					components: [
						{
							title: 'temperature_favorite_positions',
							component: 'temperature_favorite_positions',
						},
					],
				});
			} else if (!isWallDisplayThermostatEnabled && hasSensorSettings) {
				configSettings.splice(
					configSettings.findIndex((item) => item.title === 'sensor_settings'),
					1,
				);
			}
		}

		// Remove settings duplicated as sidepanel tabs
		const BLE_DEVICES_WITH_FIND_ME_TAB = ['SBRC-005B', 'SBBT-002C', 'SBBT-102C'];
		if (currentDevice?.type && BLE_DEVICES_WITH_FIND_ME_TAB.includes(currentDevice.type)) {
			configSettings = configSettings.filter(
				(item: { title: string }) => item.title !== 'find_my_device',
			);
		}
		if (currentDevice?.type === 'S3XT-0S' && currentMode === 'irrigation-controller') {
			configSettings = configSettings.filter(
				(item: { title: string }) => item.title !== 'irrigation_zones_settings',
			);
		}

		return [
			{
				title: 'details',
				components: [{ title: 'details', component: 'device_details' }],
			},
			{
				title: 'location',
				components: [{ title: 'location', component: 'device_location' }],
			},
			...(currentDevice.gen === 'GBLE' && !currentDevice.shared
				? [
						{
							title: 'bluetooth_gateway',
							components: [
								{
									title: 'bluetooth_gateway',
									component: 'bluetooth_gateway',
								},
							],
						},
					]
				: []),
			...configSettings,
		];
	};

	const ACTION_ITEM_ORDER: Record<string, number> = {
		device_reboot: 0,
		factory_reset: 1,
		factory_reset_g1: 1,
		factory_reset_ble: 1,
		transfer_device: 2,
		delete_device: 3,
	};

	$: deviceSettings = device
		? buildDeviceSettings(device, settingsMode || mode || device.mode, settings, mode)
		: [];
	$: deviceSettingsKey = deviceSettings.map((setting) => setting.title).join('|');
	let cacheActiveTabName = '';
	let lastDeviceSettingsKey = '';

	$: deviceState = getDeviceState(device.id);

	const generateNavigationItems = (tab: string) => {
		if (!device) return [];

		const hasExtractedMediaPlayer = typeof $devices[`${realID}_100`] !== 'undefined';
		const hasExtractedThermostat = typeof $devices[`${realID}_101`] !== 'undefined';

		const isThermostatEnabled = typeof settings?.['thermostat:0'] !== 'undefined';
		const isMediaPlayerEnabled = !!device?.media_player_enabled;
		const hasDeviceInformationSetting = deviceSettings.some((setting) => {
			const settingObj = typeof setting === 'object' ? setting : { title: setting };
			return (
				settingObj.title === 'device_information' ||
				settingObj?.components?.some((comp) => comp?.title === 'device_information')
			);
		});
		const deviceInformationComponent = hasDeviceInformationSetting
			? deviceSettingFormatter(
					settingsDevice,
					[
						{
							title: 'device_information',
							component: 'device_information',
						},
					],
					settings,
					status,
					tab === 'device_information' ? tab : undefined,
					true,
				).find((component) => component.titleDefault === 'device_information')
			: null;

		const items: NavigationMenuItemExtendedPropsTypes[] = deviceSettings
			.map((setting, index) => {
				const settingObj = typeof setting === 'object' ? setting : { title: setting };
				if (settingObj.title === 'device_information') return null;
				const filteredComponents =
					settingObj?.components?.filter((comp) => comp?.title !== 'device_information') ?? [];
				if (settingObj?.components?.length > 0 && filteredComponents.length === 0) return null;

				const components = deviceSettingFormatter(
					settingsDevice,
					filteredComponents,
					settings,
					status,
					settingObj.title,
					true,
				);

				const formattedTitles = new Set(components.map((c) => c.titleDefault));
				const navComponentsSource = filteredComponents.filter((comp) =>
					formattedTitles.has(comp.title),
				);
				if (settingObj?.components?.length > 0 && navComponentsSource.length === 0) {
					return null;
				}

				let innerNav =
					(settingObj.title === 'application_settings' && navComponentsSource?.length >= 1) ||
					navComponentsSource?.length > 1
						? navComponentsSource.map((comp) => {
								const formatted = components.find(
									(component) => comp.title === component.titleDefault,
								);
								return {
									id: comp.title,
									parentID: settingObj.title,
									title: comp.title,
									hideAvatar: true,
									active: tab === comp.title,
									disabled: isTabDisabled(device, $deviceState, comp.title),
									component: formatted,
									isPremiumSetting:
										Boolean(comp.premium) || Boolean(formatted?.premium),
								};
							})
						: undefined;

				if (settingObj.title === 'settings' && isXT1Device && xt1Settings.length > 0) {
					innerNav = [
						...getXT1SettingsNavigationItems(device, xt1Settings),
						...(innerNav ?? []),
					];
				}

				let disabled = isTabDisabled(device, $deviceState, settingObj.title);

				// temporary disable the fav. positions tab for gen 2 cover devices
				if (is_gen_2(device) && /favorites/.test(settingObj.title)) {
					disabled = true;
				}

				if (settingObj.title === 'share' && isActiveHomeShared($homes.list)) {
					disabled = true;
				}

				return {
					id: settingObj.title,
					title: settingObj.title,
					avatarProps: { icon: getIconFromTitle(settingObj.title) },
					active: isSettingsNavTabMatch(
						tab,
						settingObj.title,
						innerNav?.map((row) => row.title) ?? [],
					),
					disabled,
					isPremiumSetting: Boolean(settingObj.premium),
					...(innerNav && innerNav.length > 0
						? { innerNav }
						: {
								component:
									navComponentsSource?.length === 1
										? components.find(
												(component) =>
													navComponentsSource[0].title ===
													component.titleDefault,
											)
										: components.find(
												(component) =>
													settingObj.title === component.titleDefault,
											),
							}),
				};
			})
			.filter(Boolean);

		// Inject transfer_device and delete_device action items into
		// the Device Settings innerNav for the sidepanel layout
		const settingsItem = items.find((item) => item.title === 'settings');
		if (settingsItem?.innerNav && hasDeviceInformationSetting) {
			const canTransfer =
				!device.shared &&
				!device.shared_by_me &&
				!device.id.includes('_') &&
				!isWallDisplay(device.type) &&
				!is_loqed(device) &&
				!$user_data?.installer;

			const actionSettings = [
				...(canTransfer
					? [
							{
								title: 'transfer_device',
								component: 'transfer_device',
							},
						]
					: []),
				{ title: 'delete_device', component: 'delete_device' },
			];

			const actionComponents = deviceSettingFormatter(
				device,
				actionSettings,
				settings,
				status,
				undefined,
				true,
			);

			actionSettings.forEach((setting) => {
				const component = actionComponents.find((c) => c.titleDefault === setting.title);
				if (component) {
					settingsItem.innerNav.push({
						id: setting.title,
						parentID: 'settings',
						title: setting.title,
						hideAvatar: true,
						active: false,
						// FE-2007: shared homes keep the action visible but disabled;
						// an inline explanation is shown above the action buttons.
						disabled:
							isTabDisabled(device, $deviceState, setting.title) ||
							isActiveHomeShared($homes.list),
						component,
					});
				}
			});
		}

		const bleNativeSettings =
			is_GBLE(device) && !device.shared
				? (devices_config[device.type]?.modes?.[settingsMode || mode || device.mode]
						?.bluetooth_settings ?? [])
				: [];
		if (bleNativeSettings.length > 0) {
			const bleRows = bleNativeSettings.map((setting: { title: string }) => ({
				id: setting.title,
				parentID: 'settings',
				title: setting.title,
				hideAvatar: true,
				active: false,
				disabled: !$app,
				component: {
					component: BluetoothSettings,
					searchComponentKey: 'bluetooth_settings_direct',
					titleDefault: setting.title,
					props: {
						device,
						usedInSidepanel: true,
						settingTitle: setting.title,
					},
				},
			}));
			const settingsNav = items.find((item) => item.title === 'settings');
			if (settingsNav?.innerNav) {
				settingsNav.innerNav = [...bleRows, ...settingsNav.innerNav];
			} else if (settingsNav?.component) {
				settingsNav.innerNav = [
					...bleRows,
					{
						id: settingsNav.component.titleDefault ?? settingsNav.title,
						parentID: 'settings',
						title: settingsNav.component.titleDefault ?? settingsNav.title,
						hideAvatar: true,
						active: false,
						disabled: settingsNav.disabled,
						component: settingsNav.component,
					},
				];
				delete settingsNav.component;
			} else if (settingsNav) {
				settingsNav.innerNav = bleRows;
			} else {
				items.push({
					id: 'settings',
					title: 'settings',
					avatarProps: { icon: getIconFromTitle('settings') },
					active: false,
					disabled: false,
					innerNav: bleRows,
				});
			}
		}

		// Add extracted peripheral device tabs after Actions tab for wall display devices
		if (isWallDisplay(device.type) && device.category === 'relay') {
			const actionsIndex = items.findIndex((item) => item.title === 'actions');
			if (actionsIndex !== -1) {
				const extractedTabs = [];

				// Add thermostat tab if enabled
				if (isThermostatEnabled) {
					const thermostatDevice = hasExtractedThermostat ? $devices[`${realID}_101`] : device;
					if (thermostatDevice) {
						extractedTabs.push({
							id: 'thermostat',
							title: 'thermostat',
							avatarProps: {
								icon: getIconFromTitle('thermostat'),
							},
							active: tab === 'thermostat',
							disabled: false,
							component: {
								component: ThermostatsG2,
								searchComponentKey: 'thermostats_g2',
								titleDefault: 'thermostat',
								props: { device: thermostatDevice },
							},
						});
					}
				}

				// Add media player tab if enabled
				if (isMediaPlayerEnabled) {
					const mediaPlayerDevice = hasExtractedMediaPlayer
						? $devices[`${realID}_100`]
						: device;
					if (mediaPlayerDevice) {
						extractedTabs.push({
							id: 'media_player',
							title: 'media_player',
							avatarProps: {
								icon: getIconFromTitle('media_player'),
							},
							active: tab === 'media_player',
							disabled: false,
							component: {
								component: MediaPlayerComponent,
								searchComponentKey: 'media_player_direct',
								titleDefault: 'media_player',
								props: {
									device: mediaPlayerDevice,
									compact: true,
								},
							},
						});
					}
				}

				// Insert extracted tabs after Actions
				if (extractedTabs.length > 0) {
					items.splice(actionsIndex + 1, 0, ...extractedTabs);
				}
			}
		}

		if (deviceInformationComponent) {
			items.push({
				id: 'device_information',
				title: 'device_information',
				avatarProps: { icon: getIconFromTitle('device_information') },
				active: tab === 'device_information',
				disabled: isTabDisabled(device, $deviceState, 'device_information'),
				component: deviceInformationComponent,
			});
		}

		const settingsNavForActions = items.find((item) => item.title === 'settings');
		const actionLeaves = settingsNavForActions?.innerNav ?? [];
		const actionTitles = actionLeaves
			.filter((row) => SETTINGS_ACTION_TITLES.has(row.title))
			.map((row) => row.title);
		const hasFirmware = actionLeaves.some((row) => row.title === 'firmware_version');
		if (actionTitles.length === 0 && hasDeviceInformationSetting) {
			const canTransferFallback =
				!device.shared &&
				!device.shared_by_me &&
				!device.id.includes('_') &&
				!isWallDisplay(device.type) &&
				!is_loqed(device) &&
				!$user_data?.installer;
			if (canTransferFallback) actionTitles.push('transfer_device');
			actionTitles.push('delete_device');
		}

		if (actionTitles.length > 0 || hasFirmware) {
			const maintenanceFormatted = deviceSettingFormatter(
				settingsDevice,
				[
					{
						title: 'maintenance_actions',
						component: 'maintenance_actions',
					},
				],
				settings,
				status,
				undefined,
				true,
			).find((component) => component.titleDefault === 'maintenance_actions');
			if (maintenanceFormatted) {
				items.push({
					id: 'maintenance_actions',
					title: 'maintenance_actions',
					avatarProps: {
						icon: getIconFromTitle('maintenance_actions'),
					},
					active: tab === 'maintenance_actions',
					disabled: isTabDisabled(device, $deviceState, 'maintenance_actions'),
					component: {
						...maintenanceFormatted,
						props: {
							...maintenanceFormatted.props,
							actionTitles,
							showFirmware: hasFirmware,
							isOffline: status?.online === false,
						},
					},
				});
			}
		}

		return items;
	};

	let activeNavItem;
	let lastSharedHomeActive = isActiveHomeShared($homes.list);
	$: {
		const sharedHomeActive = isActiveHomeShared($homes.list);
		if (showSettingsModal && device && !initialize && sharedHomeActive !== lastSharedHomeActive) {
			lastSharedHomeActive = sharedHomeActive;
			const tab = getSettingsNavGenerateTab({
				initialize: false,
				firstCategoryTitle: deviceSettings[0]?.title,
				cachedTitle: cacheActiveTabName,
				activeItemTitle: activeNavItem?.title,
			});
			navigationItems = restoreActiveNavLeaf(generateNavigationItems(tab), activeNavItem?.id);
		}
	}

	$: {
		if (
			showSettingsModal &&
			device &&
			deviceSettings.length > 0 &&
			deviceSettingsKey !== lastDeviceSettingsKey
		) {
			const previousActiveId = initialize ? undefined : activeNavItem?.id;
			const tab = getSettingsNavGenerateTab({
				initialize,
				firstCategoryTitle: deviceSettings[0]?.title,
				cachedTitle: cacheActiveTabName,
				activeItemTitle: activeNavItem?.title,
			});
			navigationItems = restoreActiveNavLeaf(generateNavigationItems(tab), previousActiveId);
			lastDeviceSettingsKey = deviceSettingsKey;
			if (initialize) {
				initialize = false;
			}
		} else if (
			activeNavItem &&
			!activeNavItem.parentID &&
			!(activeNavItem.innerNav && activeNavItem.innerNav.length > 0)
		) {
			if (cacheActiveTabName !== activeNavItem.title) {
				navigationItems = generateNavigationItems(activeNavItem.title);
				cacheActiveTabName = activeNavItem.title;
			}
		}

		activeNavItem = findActiveItem(navigationItems);
		if (typeof activeNavItem === 'undefined') {
			const firstAvailableItem = navigationItems.find((item) => !item.disabled);
			if (firstAvailableItem) {
				onClickNavigationItem(firstAvailableItem);
			}
		}
	}

	const findActiveItem = (
		items: NavigationMenuItemExtendedPropsTypes[],
	): NavigationMenuItemExtendedPropsTypes | undefined => {
		for (const item of items) {
			// If this item has innerNav, check inside first
			if (item.innerNav && item.innerNav.length > 0) {
				const activeInner = findActiveItem(item.innerNav);
				if (activeInner) {
					return activeInner; // Return active inner item
				}
			}

			// If no active innerNav found, but this item itself is active
			if (item.active && !item.disabled) {
				return item; // Return this item as active
			}
		}

		return undefined;
	};

	$: flattenedRail = flattenSettingsRail(navigationItems, {
		showNewLayout: $showNewLayout,
	});
	$: settingsSearchSectionTitles = Object.fromEntries(
		flattenedRail.entries
			.filter((entry) => entry.kind === 'group')
			.map((entry) => [
				entry.id,
				$format(SETTINGS_SECTION_COPY[entry.id].titleKey, {
					default: SETTINGS_SECTION_COPY[entry.id].titleDefault,
				}),
			]),
	);
	$: settingsSearchDocuments = buildSettingsSearchDocuments(
		flattenedRail.entries,
		flattenedRail.detailsItem,
		$format,
		settingsSearchSectionTitles,
		deviceSettingsSearchCatalog,
	);
	$: normalizedSettingsSearchQuery = normalizeSettingsSearchText(settingsSearchQuery);
	$: settingsSearchActive = normalizedSettingsSearchQuery.length >= MIN_SETTINGS_SEARCH_QUERY_LENGTH;
	$: settingsSearchMatches = settingsSearchActive
		? searchSettingsDocuments(settingsSearchDocuments, settingsSearchQuery)
		: [];
	$: settingsSearchMatchByItem = new Map(settingsSearchMatches.map((match) => [match.item, match]));
	$: displayedRailEntries = settingsSearchActive
		? filterSettingsRailEntries(flattenedRail.entries, settingsSearchMatches)
		: flattenedRail.entries;
	$: displayedDetailsItem =
		!settingsSearchActive ||
		settingsSearchMatches.some((match) => match.item === flattenedRail.detailsItem)
			? flattenedRail.detailsItem
			: undefined;
	$: settingsSearchHasResults = Boolean(displayedDetailsItem) || displayedRailEntries.length > 0;
	$: showAdvancedRailItems = settingsSearchActive || $deviceSettingsAdvanced;

	$: roomName =
		$rooms[device?.room_id]?.name || $format('settings_details_no_room', { default: 'No room' });

	$: if (
		showSettingsModal &&
		activeNavItem &&
		!$deviceSettingsAdvanced &&
		isSettingsRailItemAdvanced(
			activeNavItem,
			getSettingsRailSectionForItem(flattenedRail.entries, activeNavItem),
		)
	) {
		const fallback = flattenedRail.entries
			.flatMap((entry) => (entry.kind === 'group' ? entry.items : [entry.item]))
			.find((item) => {
				const section = getSettingsRailSectionForItem(flattenedRail.entries, item);
				return !isSettingsRailRowCollapsed(item, false, section);
			});
		if (fallback) {
			onClickNavigationItem(fallback);
		} else if (flattenedRail.detailsItem) {
			onClickNavigationItem(flattenedRail.detailsItem);
		}
	}

	let hideLeftContent = false;
	let hideRightContent = false;

	$: {
		const pane = getSettingsModalPaneFlags({
			isMobile,
			drillIntoSetting: showSettingsModal && drillIntoSetting,
		});
		hideLeftContent = pane.hideLeftContent;
		hideRightContent = pane.hideRightContent;
	}

	$: modalTitle = !hideLeftContent
		? $format('settings', { default: 'Settings' })
		: capFirstChar(
				$format(getSettingsRailRowTitle(activeNavItem ?? { title: '' }).key, {
					default: getSettingsRailRowTitle(activeNavItem ?? { title: '' }).default,
				}),
			);

	$: modalSubtitle = (() => {
		const section = getSettingsRailSectionForItem(flattenedRail.entries, activeNavItem);
		if (!section) return '';
		return capFirstChar(
			$format(SETTINGS_SECTION_COPY[section].titleKey, {
				default: SETTINGS_SECTION_COPY[section].titleDefault,
			}),
		);
	})();

	$: deviceID = $sidePanelState.id;

	$: notificationsToShow = getNotificationsForDevice(deviceID);

	$: criticalNotifications = getCriticalNotifications(notificationsToShow);

	//Handle routeToDeviceSettings navigation (must run before notifications redirect so CTA targets are honoured)
	let pendingRoute: {
		setting: string;
		subSetting?: string;
		params?: unknown;
	} | null = null;
	let routeHandled = false;
	let skipNotificationsRedirectThisCycle = false;

	// Watch for routes and store them for processing
	$: {
		if (
			Object.keys($routeToDeviceSettings).length > 0 &&
			$routeToDeviceSettings[device.id] &&
			$routeToDeviceSettings &&
			!routeHandled
		) {
			const route = $routeToDeviceSettings[device.id];
			if (route.setting) {
				pendingRoute = {
					setting: route.setting,
					subSetting: route.subSetting,
					params: route.params,
				};
				// Open modal asynchronously to avoid cyclical dependency
				if (!showSettingsModal) {
					tick().then(() => {
						showSettingsModal = true;
					});
				}
			}
		} else if (
			Object.keys($routeToDeviceSettings).length === 0 ||
			!$routeToDeviceSettings[device.id] ||
			!$routeToDeviceSettings
		) {
			pendingRoute = null;
			routeHandled = false;
		}
	}

	// Process pending route when modal is open and navigation items are ready
	$: {
		if (pendingRoute && showSettingsModal && navigationItems.length > 0 && !routeHandled) {
			routeHandled = true;
			skipNotificationsRedirectThisCycle = true;
			const { setting, subSetting, params } = pendingRoute;
			// Determine target nav item ID: subSetting takes precedence, otherwise use setting
			const requestedId = subSetting || setting;
			const railIds = collectSettingsRailNavIds(flattenedRail);
			const targetId = resolveSettingsRailNavId(requestedId, (id) => railIds.has(id));

			const targetItem = findItemByID(navigationItems, targetId);
			if (targetItem) {
				redirectToNavItemID(targetId);
				const shouldInitiate =
					isSettingsRouteInitiate(params) && targetItem.title === 'trv_behaviour';
				if (shouldInitiate) {
					void tick().then(() => {
						if (activeNavItem?.title === 'trv_behaviour') {
							settingsRouteIntent.set({ initiate: true });
						}
					});
				}
			}
			routeToDeviceSettings.resetTarget();
			pendingRoute = null;
			setTimeout(() => {
				routeHandled = false;
			}, 100);
		}
	}

	// Manage redirects (run after route handling so routeToDeviceSettings target is applied first)
	$: $sidePanelState.open ? handleRedirectToItem() : null;
	$: showSettingsModal ? handleRedirectToItem() : null;

	const resetActiveItems = (
		items: NavigationMenuItemExtendedPropsTypes[],
	): NavigationMenuItemExtendedPropsTypes[] => {
		return items.map((item, i) => ({
			...item,
			active: false,
			...(item.innerNav && { innerNav: resetActiveItems(item.innerNav) }),
		}));
	};

	const handleDetailsSaved = (payload: {
		roomChanged: boolean;
		roomId?: number;
		shared?: boolean;
	}) => {
		onCloseSettings();
		if (payload.roomChanged && payload.roomId !== undefined) {
			setSidePanelState({ open: false });
			if (payload.shared && payload.roomId === -20) {
				replace('/home/devices/-20');
			} else {
				replace(`/home/room/${payload.roomId}/devices`);
			}
		}
	};

	setContext('onDetailsSaved', handleDetailsSaved);

	const stickyBottomAction = writable<{
		onClick: () => void;
		loading: boolean;
		disabled: boolean;
		label?: string;
		secondaryButton?: {
			onClick: () => void;
			label: string;
			variant?: string;
		};
		gap?: string;
	} | null>(null);
	setContext('stickyBottomAction', stickyBottomAction);

	const onCloseSettings = () => {
		showSettingsModal = false;
		setSidePanelState({ showSettingsModal: undefined });
		drillIntoSetting = false;

		if (isMobile && $app) {
			if ($controlBleDevice.connectionState === 2) {
				$os === 'IOS'
					? control_bluetooth_device_ios(device.id, 'disconnect')
					: control_bluetooth_device_android(device.id, 'disconnect');
			} else {
				$os === 'IOS'
					? control_bluetooth_device_ios(device.id, 'abortConnecting')
					: control_bluetooth_device_android(device.id, 'disconnect');
			}
			controlBleDevice.reset();
		}

		// Clear active items and set first as active
		navigationItems = resetActiveItems(navigationItems);
		navigationItems[0].active = true;
		activeNavItem = navigationItems[0];
		initialize = true;
		cacheActiveTabName = '';
		lastDeviceSettingsKey = '';
		skipNotificationsRedirectThisCycle = false;
		settingsSearchQuery = '';
	};

	const setActiveNavItem = (
		items: NavigationMenuItemExtendedPropsTypes[],
		activeItem: NavigationMenuItemExtendedPropsTypes,
	) => {
		return items.map((item) => {
			const updatedInnerNav: NavigationMenuItemExtendedPropsTypes[] = item.innerNav
				? setActiveNavItem(item.innerNav, activeItem)
				: [];

			return {
				...item,
				active: item.id === activeItem?.id || updatedInnerNav.some((item) => item.active),
				...(updatedInnerNav?.length && { innerNav: updatedInnerNav }),
			};
		});
	};

	const restoreActiveNavLeaf = (items: NavigationMenuItemExtendedPropsTypes[], activeId?: string) => {
		if (!activeId) return items;
		const restored = findItemByID(items, activeId);
		if (!restored) return items;
		return setActiveNavItem(items, restored);
	};

	const onClickNavigationItem = (selectedItem?: NavigationMenuItemExtendedPropsTypes) => {
		if (!selectedItem) return;

		const section = getSettingsRailSectionForItem(flattenedRail?.entries ?? [], selectedItem);
		if (isSettingsRailItemAdvanced(selectedItem, section)) {
			$deviceSettingsAdvanced = true;
		}
		navigationItems = setActiveNavItem(navigationItems, selectedItem);
		activeNavItem = selectedItem;
		cacheActiveTabName = selectedItem.title;
	};

	const handleNavItemClick = (selectedItem: NavigationMenuItemExtendedPropsTypes) => {
		drillIntoSetting = true;
		onClickNavigationItem(selectedItem);
	};

	const findItemByID = (
		items: NavigationMenuItemExtendedPropsTypes[],
		activeId: string,
	): NavigationMenuItemExtendedPropsTypes => {
		for (const item of items) {
			// If this item has innerNav, check inside first
			if (item?.innerNav) {
				const activeInner = findItemByID(item.innerNav, activeId);
				if (activeInner) {
					return activeInner; // Return active inner item
				}
			}

			// If no active innerNav found
			if (item.id === activeId) {
				return item; // Return this item as active
			}
		}

		return undefined;
	};

	const onBack = () => {
		drillIntoSetting = false;
	};

	setContext('onSettingsDrilldownBack', () => {
		if (!isMobile) return;
		onBack();
	});

	export const redirectToNavItemID = (navItemID: string) => {
		if (!showSettingsModal) showSettingsModal = true;
		activeNavItem = findItemByID(navigationItems, navItemID);
		navigationItems = setActiveNavItem(navigationItems, activeNavItem);
		if (activeNavItem?.title) cacheActiveTabName = activeNavItem.title;
		// Defer so deep-link $: blocks do not write drillIntoSetting in the same
		// reactive graph as navigationItems / initialize (cycle).
		void tick().then(() => {
			drillIntoSetting = true;
		});
	};

	const redirectToNotifications = () => {
		if (!criticalNotifications?.length) return;
		if (!showSettingsModal) return; // Only redirect when user has already opened settings; do not auto-open the modal
		// Skip when we just processed a routeToDeviceSettings target (e.g. CTA to temperature_favorite_positions).
		if (skipNotificationsRedirectThisCycle) return;
		if (routeHandled) return; // Explicit route was just applied; don't override for 100ms.
		// When the user explicitly navigated to a setting via routeToDeviceSettings, honour that.
		if (
			Object.keys($routeToDeviceSettings).length > 0 &&
			$routeToDeviceSettings[device?.id]?.setting
		)
			return;

		const notificationsID = 'notifications';
		// New rail has no Notifications tab; a missing target clears the active item and leaves the Loader up.
		if (!findItemByID(navigationItems, notificationsID)) return;
		redirectToNavItemID(notificationsID);
	};

	const handleRedirectToItem = () => redirectToNotifications();

	onMount(() => {
		if (mobile.isMobile) {
			mobile.addEventListener('back_button', onBack);
		}

		if (isXT1Device) {
			fetchXT1DeviceSettings(device).then((settings) => {
				xt1Settings = settings;
				const activeTab =
					activeNavItem?.parentID || activeNavItem?.title || deviceSettings[0]?.title;
				navigationItems = generateNavigationItems(activeTab);
				cacheActiveTabName = activeTab;
			});
		}

		return () => {
			if (mobile.isMobile) {
				mobile.removeEventListener('back_button', onBack);
			}
		};
	});
</script>

<Modal
	type={isMobile ? 'fullScreen' : 'default'}
	on:close={onCloseSettings}
	showModal={showSettingsModal}
	title={modalTitle}
	subtitle={isMobile ? modalSubtitle : ''}
	customContent
	hideSlotHeaderActionsLeft={!hideLeftContent}
	preserveMaxHeight={!isMobile}
>
	<svelte:fragment slot="headerActionsLeft">
		<IconButton
			aria-label="Back Button"
			variant="secondary"
			icon="fas fa-arrow-left"
			on:click={onBack}
		/>
	</svelte:fragment>

	<svelte:fragment slot="headerActions">
		{#if !hideLeftContent}
			<Toggle
				id="device-settings-advanced"
				size="m"
				class="d-inline-flex flex-shrink-0"
				bind:checked={$deviceSettingsAdvanced}
			>
				{$format('label_advanced', { default: 'Advanced' })}
			</Toggle>
		{/if}
	</svelte:fragment>

	<ModalContentLayout
		slot="content"
		{hideRightContent}
		{hideLeftContent}
		leftContainer={false}
		hideStaticTop
		hideTopContent
		bind:isMobile
		hideStickyBottom={!$stickyBottomAction}
		hideScrollbar
		leftEdgeDissolve="bottom"
	>
		<div slot="leftStickyTop" class="d-flex flex-direction-column width-full gap-s">
			{#if flattenedRail.detailsItem}
				{@const details = flattenedRail.detailsItem}
				{@const detailsSearchMatch = settingsSearchMatchByItem.get(details)}
				<Container type="borderSolid" padding="0" backgroundColor="color-base" noScroll>
					<NavigationItem
						title={device.name}
						active={activeNavItem?.id === details.id}
						disabled={details.disabled}
						avatarProps={{
							image: getDeviceImage(device),
							icon: getDeviceIcon(device),
							backgroundColor: device.backgroundColor,
						}}
						type={isMobile ? 'drilldown' : 'default'}
						drillDownNoBorder
						on:click={() => handleNavItemClick(details)}
					>
						{#if settingsSearchActive && detailsSearchMatch?.matchedContent}
							<SettingsSearchHighlightedContent
								slot="subtitle"
								title=""
								subtitle={detailsSearchMatch.matchedContent.text}
								query={settingsSearchQuery}
								active={activeNavItem?.id === details.id}
								{isMobile}
							/>
						{:else}
							<Text
								slot="subtitle"
								styleType="body-small"
								class={activeNavItem?.id === details.id && !isMobile
									? 'text-color-3'
									: 'text-color-muted'}
							>
								{roomName}
							</Text>
						{/if}
					</NavigationItem>
				</Container>
			{/if}
			<Input
				id="device-settings-search"
				bind:value={settingsSearchQuery}
				label={$format('search', {
					default: 'Search',
				})}
				aria-label={$format('search', {
					default: 'Search',
				})}
				icon="fas fa-search"
				width="full"
				clearButton
				a11yProps={{
					iconButtonAriaLabel: $format('button_clear', {
						default: 'Clear',
					}),
				}}
				on:escape={() => (settingsSearchQuery = '')}
			/>
		</div>
		<div slot="leftContent" class="d-flex flex-direction-column width-full gap-s">
			{#if settingsSearchActive && !settingsSearchHasResults}
				<NoSearchResults search={settingsSearchQuery.trim()} marginTop="m" />
			{/if}
			{#each displayedRailEntries as entry (entry.kind === 'group' ? entry.id : (entry.item.id ?? entry.item.title))}
				{#if entry.kind === 'group'}
					{@const lastVisibleIndex = entry.items.reduce(
						(last, row, idx) =>
							isSettingsRailRowCollapsed(row, showAdvancedRailItems, entry.id)
								? last
								: idx,
						-1,
					)}
					<LegendContainer
						title={capFirstChar(
							$format(SETTINGS_SECTION_COPY[entry.id].titleKey, {
								default: SETTINGS_SECTION_COPY[entry.id].titleDefault,
							}),
						)}
						collapsed={isSettingsRailGroupCollapsed(
							entry.items,
							showAdvancedRailItems,
							entry.id,
						)}
					>
						{#each entry.items as item, i (item.id ?? item.title)}
							{@const rowTitle = getSettingsRailRowTitle(item)}
							{@const rowTitleText = capFirstChar(
								$format(rowTitle.key, {
									default: rowTitle.default,
								}),
							)}
							{@const searchMatch = settingsSearchMatchByItem.get(item)}
							{@const premiumState = getSettingsRailPremiumState(
								item,
								!!$user_data?.premium,
							)}
							<NavigationItem
								hideAvatar
								drillDownNoBorder={i === lastVisibleIndex}
								title={settingsSearchActive ? '' : rowTitleText}
								active={item.active || activeNavItem?.id === item.id}
								disabled={item.disabled}
								type={isMobile ? 'drilldown' : 'default'}
								advanced={isSettingsRailItemAdvanced(item, entry.id)}
								collapsed={isSettingsRailRowCollapsed(
									item,
									showAdvancedRailItems,
									entry.id,
								)}
								premium={premiumState}
								on:click={() => handleNavItemClick(item)}
							>
								{#if settingsSearchActive}
									<SettingsSearchHighlightedContent
										title={rowTitleText}
										subtitle={searchMatch?.matchedContent?.text ?? ''}
										query={settingsSearchQuery}
										active={item.active || activeNavItem?.id === item.id}
										disabled={item.disabled}
										{isMobile}
										premium={premiumState}
									/>
								{/if}
								<LabelV2
									size="s"
									slot="trailing"
									{...handleCollapseTitleLabel(
										item.component ?? {
											titleDefault: item.title,
										},
									)}
								/>
							</NavigationItem>
						{/each}
					</LegendContainer>
				{:else}
					{@const rowTitle = getSettingsRailRowTitle(entry.item)}
					{@const rowTitleText = capFirstChar(
						$format(rowTitle.key, { default: rowTitle.default }),
					)}
					{@const searchMatch = settingsSearchMatchByItem.get(entry.item)}
					{@const premiumState = getSettingsRailPremiumState(
						entry.item,
						!!$user_data?.premium,
					)}
					{@const notificationState =
						entry.item.title === 'maintenance_actions' && rebootPending
							? 'danger'
							: undefined}
					<NavigationItem
						hideAvatar
						title={settingsSearchActive ? '' : rowTitleText}
						active={entry.item.active || activeNavItem?.id === entry.item.id}
						disabled={entry.item.disabled}
						type={isMobile ? 'drilldown' : 'default'}
						advanced={isSettingsRailItemAdvanced(entry.item)}
						collapsed={isSettingsRailRowCollapsed(entry.item, showAdvancedRailItems)}
						premium={premiumState}
						notification={settingsSearchActive ? undefined : notificationState}
						on:click={() => handleNavItemClick(entry.item)}
					>
						{#if settingsSearchActive}
							<SettingsSearchHighlightedContent
								title={rowTitleText}
								subtitle={searchMatch?.matchedContent?.text ?? ''}
								query={settingsSearchQuery}
								active={entry.item.active || activeNavItem?.id === entry.item.id}
								disabled={entry.item.disabled}
								{isMobile}
								premium={premiumState}
								notification={notificationState}
							/>
						{/if}
						<LabelV2
							size="s"
							slot="trailing"
							{...handleCollapseTitleLabel(
								entry.item.component ?? {
									titleDefault: entry.item.title,
								},
							)}
						/>
					</NavigationItem>
				{/if}
			{/each}
		</div>

		<div slot="content">
			{#if !settingsReady}
				{#if settingsReadiness.phase === 'error'}
					<Alert
						type="danger"
						heading={$format('error_global')}
						message={$format('error_global')}
						isHeadingBold
					/>
				{:else}
					<div class="d-flex justify-content-center align-items-center height-full">
						<Loader size="m" />
					</div>
				{/if}
			{:else if activeNavItem}
				{#if device.gen === 'GBLE' && !device.shared && (activeNavItem.parentID === 'settings' || activeNavItem.title === 'settings')}
					{#if !$app}
						<Alert
							type="warning"
							isHeadingBold
							heading={$format('alert_heading_ble_devices_restricted', {
								default: 'Settings Restricted',
							})}
							message={$format('alert_message_ble_devices_restricted', {
								default:
									'Bluetooth devices can be configured only via the Shelly Smart Control application.',
							})}
							class="mb-m"
						/>
					{/if}

					{#if $app && !isSupportedVersion($os === 'IOS' ? '1.0.5' : '1.14.0')}
						<Alert
							type="warning"
							heading={$format('alert_heading_native_update_required', {
								default: 'App update required',
							})}
							message={$format('alert_message_native_update_required', {
								default:
									'Please update the application to the latest version for full support of these features.',
							})}
							class="mb-m"
						/>
					{/if}
				{/if}
				{#if device && activeNavItem?.component}
					<div
						class="height-full"
						use:highlightSettingsSearchContent={settingsSearchActive
							? settingsSearchQuery
							: ''}
					>
						<DeviceSettingsItem {device} setting={activeNavItem.component} />
					</div>
				{/if}
			{:else}
				<div class="d-flex justify-content-center align-items-center">
					<Loader size="m" />
				</div>
			{/if}
		</div>
		<SidePanelSettingsModalStickyBottom slot="stickyBottom" />
	</ModalContentLayout>
</Modal>

<style>
	:global(::highlight(settings-search-content-highlight)) {
		background-color: rgba(var(--color-primary-1-rgb, 0, 123, 255), 0.3);
		color: var(--color-primary-1);
	}

	:global(.settings-search-content-highlight) {
		padding: 0;
		background-color: rgba(var(--color-primary-1-rgb, 0, 123, 255), 0.3);
		color: var(--color-primary-1);
	}
</style>
